import { EmeraldIconButton } from '@emerald-react/icon-button';
import { EmeraldInput } from '@emerald-react/input';
import { EmeraldAvatar } from '@emerald-react/avatar';
import { EmeraldButton } from '@emerald-react/button';
import React, { useContext, useEffect, useRef, useState } from 'react';

// @ts-ignore
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
// @ts-ignore
import { atomDark } from 'react-syntax-highlighter/dist/esm/styles/prism';
import TreeStructure from '../treeStructure/TreeStructure';
import {
  downloadZip,
  emitter,
  extractFileNamesAndCode,
  getDynamicCode,
  getEmeraldPrompt,
  includeFolderInFileStructure,
} from '../constants';
import { PromptResponse } from './prompt-response';
import { generateId } from '../../utils/Utils';
import { useApi } from '@backstage/core-plugin-api';
import { promptApiRef } from '../../../apis/promptManagementApi';
import { useUserProfile } from '../../utils/useGetUserProfile';
import { AuthContext } from '../../../providers/AuthProvider';
import {
  getAngularInitialPrompts,
  getAssetsFolderTemplate,
} from '../project-structures/AngularProjectStructure';
import {
  getPublicFolderTemplate,
  getReactInitialPrompts,
} from '../project-structures/ReactProjectStructure';
// import { Avatar } from '@backstage/core-components';

export const GeneratedCodeDisplay = (props: any) => {
  const id = generateId();
  const promptApi = useApi(promptApiRef);
  const { accessToken, profile } = useUserProfile();
  const { profEmail, profileName } = useContext(AuthContext);
  const [promptValue, setPromptValue] = useState('');
  const assetsRef = useRef<any>({});
  const resultDataRef = useRef<any>({});
  const [chat, setChat] = useState<{ content: string; role: string }[]>([]);
  const [generateCode, setGenerateCode] = useState<any>(false);
  const [codegenCompleted, setCodegenCompleted] = useState<any>(false);
  const [userName, setUserName] = useState<string>('');
  const [showRawResponse, setShowRawResponse] = useState<boolean>(false);
  const historyChangeRef = useRef<any>(null);
  const [historyChange, setHistoryChange] = useState<any>(
    historyChangeRef.current,
  );
  const isTestingLocal = false;
  let promptResponse: PromptResponse | null = null;

  const getCodegenPromptInitial = () => {
    return {
      initalPrompt: [],
      userPrompt: [],
    };
  };
  const [codegenPrompt, setCodegenPrompt] = useState<any>({
    ...getCodegenPromptInitial(),
  });
  const getEmptyCodeSectionData = () => {
    return {
      filesWithFolders: [],
      currentFileContent: {
        code: '',
        language: '',
      },
      selectedFile: null,
      configSelection: {},
      userPrompt: '',
      isEmeraldPrompt: false,
    };
  };
  const [presentCodegenData, setPresentCodegenData] = useState<any>({
    ...getEmptyCodeSectionData(),
  });
  const [oldCodegenData, setOldCodegenData] = useState<any>([]);
  const currentConfig = useRef<any>({});

  const updatePresentData = (index: number) => {
    const lastData = oldCodegenData[index];
    setPresentCodegenData(lastData?.codeSection || getEmptyCodeSectionData());
  };

  const selectedOldDataIndexHandler = (data: any) => {
    if (data.index >= 0 && data.index < oldCodegenData.length) {
      updatePresentData(data.index);
    }
  };

  useEffect(() => {
    emitter.on('selectedOldDataIndex', selectedOldDataIndexHandler.bind(this));
    return () =>
      emitter.off('selectedOldDataIndex', selectedOldDataIndexHandler);
  }, [oldCodegenData]);

  useEffect(() => {
    if (oldCodegenData.length > 0) {
      updatePresentData(oldCodegenData.length - 1);
      emitter.emit('oldCodegenDataUpdate', {
        oldData: oldCodegenData,
        selectedIndex: oldCodegenData.length - 1,
      });
    }
  }, [oldCodegenData]);

  const historySelectedHandler = (data: any) => {
    if (data?.key) {
      const [
        frameworkSelected,
        frameworkVersionSelected,
        frameworkStyleSelected,
      ] = data.key.split('-');
      currentConfig.current = {
        frameworkSelected,
        frameworkVersionSelected,
        frameworkStyleSelected,
      };
      updateHistory(data);
    } else {
      updateHistory({});
    }
  };

  const getSystemPrompt = (useEmerald = false) => {
    const getFrameworkInitialPrompts =
      currentConfig.current.frameworkSelected === 'React'
        ? getReactInitialPrompts
        : getAngularInitialPrompts;
    return getFrameworkInitialPrompts(
      resultDataRef.current.html,
      resultDataRef.current.css,
      currentConfig.current.frameworkVersionSelected,
      currentConfig.current.frameworkStyleSelected || 'scss',
      assetsRef.current?.projectName,
      useEmerald,
    ) as any[];
  };

  const triggerAIHandler = (configSelection: any) => {
    currentConfig.current = configSelection;
    setCodegenPrompt(() => {
      const temp: any = getCodegenPromptInitial();

      temp.initalPrompt = getSystemPrompt();
      temp.userPrompt = [];
      return temp;
    });
    setPresentCodegenData((prev: any) => {
      return {
        ...getEmptyCodeSectionData(),
        configSelection: currentConfig.current,
      };
    });
    setGenerateCode(true);
  };

  const assetsUpdateHandler = (data: any) => (assetsRef.current = data);
  const codegenHandler = (data: any) => (resultDataRef.current = data);

  useEffect(() => {
    emitter.on('assetsUpdate', assetsUpdateHandler.bind(this));
    emitter.on('codegen:html:css', codegenHandler.bind(this));
    emitter.on('historySelected', historySelectedHandler.bind(this));
    emitter.on('triggerAI', triggerAIHandler.bind(this));
    emitter.on('emeraldPrompt', emeraldPromptHandler.bind(this));
    return () => {
      emitter.off('assetsUpdate', assetsUpdateHandler);
      emitter.off('historySelected', historySelectedHandler);
      emitter.off('triggerAI', triggerAIHandler);
      emitter.off('codegen:html:css', codegenHandler);
      emitter.off('emeraldPrompt', emeraldPromptHandler);
    };
  }, []);

  useEffect(() => {
    if (!promptResponse && promptApi && profEmail && accessToken) {
      updateUSerName();
      promptResponse = getPromptResponse();
    }
  }, [promptApi, profEmail, profile, accessToken, id]);

  useEffect(() => {
    if (generateCode) {
      setGenerateCode(false);
      setTimeout(() => triggerAI(), 200);
    }
  }, [generateCode]);

  useEffect(() => {
    if (codegenCompleted) {
      setCodegenCompleted(false);
      setPresentCodegenData((prev: any) => {
        const temp: any = structuredClone(prev);
        const config = currentConfig.current;
        if (!assetsRef.current?.isImportedDesign) {
          if (config.frameworkSelected === 'React') {
            temp.filesWithFolders = includeFolderInFileStructure(
              temp.filesWithFolders,
              getPublicFolderTemplate(assetsRef.current?.images),
              assetsRef.current?.projectName || 'Test',
            ) as any[];
          } else {
            temp.filesWithFolders = includeFolderInFileStructure(
              temp.filesWithFolders,
              getAssetsFolderTemplate(assetsRef.current?.images),
              'src',
            ) as any[];
          }
        }

        temp.code_gen_id = getDynamicCode();
        temp.sessionId = getDynamicCode();
        const _oldCodegenData = structuredClone(oldCodegenData);
        _oldCodegenData.push({
          codeSection: temp,
        });
        setOldCodegenData(_oldCodegenData);
        const _chat = structuredClone(chat);
        const historySave: any = {
          key: `${config.frameworkSelected}-${config.frameworkVersionSelected}-${config.frameworkStyleSelected}`,
          chatResult: _chat,
          codegenPrompt: structuredClone(codegenPrompt),
          oldCodegenData: _oldCodegenData,
        };

        historyChangeRef.current = historySave;
        setHistoryChange(historyChangeRef.current);
        return temp;
      });
    }
  }, [codegenCompleted, chat, codegenPrompt, oldCodegenData]);

  useEffect(() => {
    if (historyChange) {
      emitter.emit('codegenResult', historyChangeRef.current);
      historyChangeRef.current = null;
      setHistoryChange(null);
    }
  }, [historyChange]);

  const getPromptResponse = () => {
    if (!promptResponse) {
      promptResponse = new PromptResponse(
        promptApi,
        profEmail,
        accessToken,
        id,
        '07b6cb62-7f19-48d4-a896-249e449f23a0',
      );
    }
    return promptResponse;
  };

  const updateUSerName = () => {
    setUserName(profileName?.split('@')[0]);
  };

  const updateHistory = (data: any) => {
    setChat(data.chatResult || []);
    setCodegenPrompt(data.codegenPrompt || getCodegenPromptInitial());
    setOldCodegenData(data.oldCodegenData || []);
  };

  const updatePrompt = () => {
    setPresentCodegenData((prev: any) => {
      return {
        ...getEmptyCodeSectionData(),
        configSelection: currentConfig.current,
        userPrompt: promptValue,
      };
    });

    const _codegenPrompt = structuredClone(codegenPrompt);
    _codegenPrompt.userPrompt.push({
      content: promptValue,
      role: 'user',
    });
    setCodegenPrompt(_codegenPrompt);
    setGenerateCode(true);
    setPromptValue('');
  };

  const emeraldPromptHandler = (data: any) => {
    const { prompt, components } = getEmeraldPrompt(
      resultDataRef.current.html,
      data.frameworkSelected?.toLowerCase(),
    );
    if (!prompt || !components) {
      alert('No reusable Emerald components found to apply...');
      return;
    }
    setPresentCodegenData((prev: any) => {
      return {
        ...getEmptyCodeSectionData(),
        configSelection: currentConfig.current,
        userPrompt: `Emerald Code Generation, found ${components} components and trying to apply while generating the code...`,
        isEmeraldPrompt: true,
      };
    });

    const _codegenPrompt = structuredClone(codegenPrompt);
    if (
      !_codegenPrompt.initalPrompt ||
      _codegenPrompt.initalPrompt.length === 0
    ) {
      _codegenPrompt.initalPrompt = getSystemPrompt();
    }
    _codegenPrompt.userPrompt.push({
      content: prompt,
      role: 'system',
    });
    setCodegenPrompt(_codegenPrompt);
    setGenerateCode(true);
    setPromptValue('');
  };

  const findAndUpdateFolderToogle = (nodes: any[], node: any) => {
    nodes.forEach((each: any) => {
      if (each.type === 'folder') {
        if (each.refrenceId === node.refrenceId) {
          each.open = !each.open;
        } else {
          if (each.children) {
            findAndUpdateFolderToogle(each.children, node);
          }
        }
      }
    });
    return nodes;
  };
  const handleFolderToggle = (node: any, index: number) => {
    const tempData: any =
      index === -1 ? { ...presentCodegenData } : [...oldCodegenData];
    const stateUpdate =
      index === -1 ? setPresentCodegenData : setOldCodegenData;
    if (node.type === 'folder') {
      if (index === -1) {
        tempData.filesWithFolders = [
          ...findAndUpdateFolderToogle(tempData.filesWithFolders, node),
        ];
      } else {
        tempData[index].codeSection.filesWithFolders = [
          ...findAndUpdateFolderToogle(
            tempData[index].codeSection.filesWithFolders,
            node,
          ),
        ];
      }
    } else {
      if (index === -1) {
        tempData.selectedFile = node;
      } else {
        tempData[index].codeSection.selectedFile = node;
      }
    }
    stateUpdate(tempData);
  };

  const appendToChat = (prev: any[], data: string, role?: string) => {
    const temp = [...prev];
    const lastElement = temp?.pop();
    let d = data ? data : '\n';
    if (lastElement) {
      lastElement.content = lastElement.content += d;
    }
    const chatData = [
      ...temp,
      lastElement || { content: d, role: role || 'assistant' },
    ];
    return chatData;
  };

  const onResponseStart = () => {
    emitter.emit('codeGenerationInProcess', true);
    // start
    setChat((prev: any[]) => {
      return [
        ...prev,
        {
          content: '',
          role: 'assistant',
        },
      ];
    });
  };

  const onResponseStop = () => {
    emitter.emit('codeGenerationInProcess', false);
    // stop
    setCodegenCompleted(true);
    emitter.emit('showOrHideLoading', false);
  };

  const onResponseData = (data: any, role?: any) => {
    // onData
    setChat((prev: any[]) => {
      const chatData = appendToChat(prev, data, role);
      extractFileNamesAndCode(
        //chatData.map((each: any) => each?.content).join(''),
        chatData[chatData.length - 1]?.content,
        (_filesWithFolders: any[]) => {
          setPresentCodegenData((prev: any) => {
            const temp = structuredClone(prev);
            temp.filesWithFolders = _filesWithFolders;
            return temp;
          });
        },
        (fileContent: any) => {
          setPresentCodegenData((prev: any) => {
            const temp = structuredClone(prev);
            temp.currentFileContent = fileContent;
            return temp;
          });
        },
        currentConfig.current.frameworkSelected === 'React',
      );
      return chatData;
    });
  };

  const onResponseError = () => {
    emitter.emit('codeGenerationInProcess', false);
    setChat((prev: any[]) => {
      let temp = [...prev];
      if (prev?.[prev.length - 1]?.role === 'assistant') {
        temp = temp.pop();
      }
      return [
        ...temp,
        {
          content: 'Error occured',
          role: 'assistant',
        },
      ];
    });
  };

  const triggerAI = () => {
    promptResponse = getPromptResponse();
    if (promptResponse) {
      emitter.emit('showOrHideLoading', true);
      promptResponse.useAIServiceStream = true;

      promptResponse.getResponse(
        [...codegenPrompt.initalPrompt, ...codegenPrompt.userPrompt],
        onResponseStart,
        onResponseStop,
        onResponseData,
        onResponseError,
      );
    }
  };

  const getFormatedDisplay = (text: string) => {
    if (!text) return '';
    return text
      ?.replace(/^\n+|\n+$/g, '')
      ?.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
  };

  const PromptDisplaySection = (props: any) => {
    const { profileName, profile, userName, userPrompt } = props;
    return (
      <div className="user-prompt-container">
        <span className="user-name-placeholder">{profileName}</span>
        <div className="user-avatar-details">
          {profile?.picture && <EmeraldAvatar imageUrl={profile?.picture} />}
          {!profile?.picture && userName && (
            <EmeraldAvatar
              initials={
                userName.charAt(0) + userName.split(',')[1].trim().charAt(0)
              }
            />
          )}
          {!profile?.picture && !userName && <EmeraldAvatar />}
          <p className="user-prompt-display">{userPrompt}</p>
        </div>
      </div>
    );
  };

  const CodeContainerDisplay = (props: any) => {
    return (
      <>
        {props.filesWithFolders?.length > 0 && (
          <TreeStructure
            key={id}
            data={props.filesWithFolders}
            onFolderToggle={(node: any) =>
              handleFolderToggle(node, props.index)
            }
            selectedFile={props.selectedFile}
          />
        )}

        <div className="code-container">
          {props.currentFileContent?.language === 'text' ? (
            <div className="prompt-response-text-container">
              <span className="agent-name-placeholder">
                CBRE Design To Code Agent
              </span>
              <div className="prompt-response-text">
                <EmeraldAvatar icon="auto_awesome" />
                <pre
                  dangerouslySetInnerHTML={{
                    __html: getFormatedDisplay(
                      props.selectedFile?.content ||
                        props.currentFileContent?.code,
                    ),
                  }}
                ></pre>
              </div>
            </div>
          ) : (
            <SyntaxHighlighter
              language={
                props.selectedFile?.language ||
                props.currentFileContent?.language
              }
              style={atomDark}
            >
              {props.selectedFile?.content || props.currentFileContent?.code}
            </SyntaxHighlighter>
          )}
        </div>

        {props.filesWithFolders?.length > 0 && (
          <div className="download-container">
            <EmeraldIconButton
              icon="file_download"
              onClick={() => {
                emitter.emit('performMetrics', { ...props, type: 'Download' });
                downloadZip(props.filesWithFolders);
              }}
            />
          </div>
        )}
      </>
    );

    // return (

    // );
  };
  // const handleTextFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
  //   const file = event.target.files?.[0];
  //   if (file) {
  //     const reader = new FileReader();
  //     reader.onload = e => {
  //       extractFileNamesAndCode(e.target?.result);
  //     };
  //     reader.readAsText(file);
  //   }
  // };
  return (
    <div className="display-content-container">
      {isTestingLocal && (
        <EmeraldButton
          label="Show Raw Response"
          onClick={() => setShowRawResponse(!showRawResponse)}
        />
      )}
      {/* <input type="file" onChange={handleTextFileChange} /> */}
      <div className="files-code-section">
        {presentCodegenData.userPrompt && (
          <PromptDisplaySection
            profileName={profileName}
            profile={profile}
            userName={userName}
            userPrompt={presentCodegenData.userPrompt}
          />
        )}
        {(presentCodegenData?.filesWithFolders?.length > 0 ||
          presentCodegenData?.selectedFile?.language ||
          presentCodegenData?.currentFileContent?.language) && (
          <div className="code-section-block">
            <CodeContainerDisplay
              index={-1}
              selectedFile={presentCodegenData.selectedFile}
              currentFileContent={presentCodegenData.currentFileContent}
              filesWithFolders={presentCodegenData.filesWithFolders}
            />
          </div>
        )}
      </div>
      <div className="prompt-code-section">
        <EmeraldInput
          label="Please enter a prompt to enhance the code"
          rows={3}
          type="textarea"
          value={promptValue}
          onChange={(event, changedValue) => setPromptValue(changedValue)}
          disabled={!oldCodegenData?.length}
        />
        <EmeraldIconButton
          icon="send"
          onClick={() => updatePrompt()}
          disabled={!promptValue}
        />
      </div>
      {showRawResponse && (
        <textarea
          style={{ width: '100%', height: '600px' }}
          value={chat?.map((each: any) => each?.content)?.join('')}
        ></textarea>
      )}
    </div>
  );
};
